import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { View } from 'react-native';
import { Icon } from '@photosi/rn-components/icon/Icon';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import TextLato from '@photosi/rn-components/text/text-lato/TextLato';
import { headerEditorTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import React from 'react';
import { useHeartBeatAnimation } from 'src/hooks/useHeartBeatAnimation';
import Animated from 'react-native-reanimated';
import { style } from './PhotoSlotButtonDesktop.style';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { BaseHoverTooltip } from '@photosi/rn-components/tooltips/base-hover-tooltip/BaseHoverTooltip';
import { FeatureFlagAmplitude } from 'src/_shared/featureFlag';
import { GhostButton } from '../buttons/ghost-button/GhostButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from '../buttons/button/Button.constants';
import { PhotoSlotButtonProps } from './PhotoSlotButton.types';

export const PhotoSlotButtonDesktop = observer(({ focusGroup, focusId, focusOrder }: PhotoSlotButtonProps) => {
  const {
    i18n,
    drawer,
    project: {
      activeGroup,
      addProductsV2,
      activeProject,
      showNotificationPhotoDrawer,
      productConfigurationId,
      groupCode,
    },
  } = useStore();

  const showWarning = showNotificationPhotoDrawer;
  const { heartbeatAnimatedStyle } = useHeartBeatAnimation({
    showWarning: showWarning,
  });
  const { isDesktop } = useResponsiveScreen();
  const isMVP4 = amplitude.getVariantValue(FeatureFlagAmplitude.EDITOR_UNICO_HEADER_V4);

  const addPhotos = async () => {
    const id = await activeProject?.getDefaultProductIdByGroup(activeGroup);
    activeGroup?.checkMinMaxProductsAndNotify();
    addProductsV2({
      ids: activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
      productConfigurationId: id || activeGroup?.productConfigurationIdDefault || productConfigurationId,
      groupCode: activeGroup?.code || groupCode,
      groupId: activeGroup?.groupIdSDK,
    });
    drawer.closeDrawer();
  };

  const onPress = async (): Promise<void> => {
    activeProject?.deselectAllThumbnailImages();
    drawer.openDrawer({
      drawerPosition: DRAWER_POSITIONS.Left,
      drawerView: DRAWER_VIEWS.PhotoDrawer,
      withBackdrop: false,
      openPoint: 'personalize',
      uploadButtonCallback: addPhotos,
    });
    amplitude.trackEvent(AnalyticsEvents.personalize.yourPhotoClick, {
      context: activeGroup ? 'products_view' : 'groups_view',
    });
  };

  const renderer = (
    <GhostButton
      text={i18n.t('header:photo')}
      size={BUTTON_SIZES.REGULAR}
      type={BUTTON_TYPES.ICON_LEFT}
      iconName={ICON_NAMES.PhotoAlbum}
      layout={BUTTON_LAYOUTS.HUG}
      accessible={true}
      // Dynamic label: announce badge state so screen readers know new photos are available
      accessibilityLabel={showNotificationPhotoDrawer ? i18n.t('header:photo-notification') : i18n.t('header:photo')}
      accessibilityRole="button"
      testID={headerEditorTestID(TestIDs.PHOTO_DRAWER)}
      onPress={onPress}
      focusId={focusId}
      focusOrder={focusOrder}
      focusGroup={focusGroup}
      badgeRenderer={
        showNotificationPhotoDrawer && (
          <View style={style.iconBadge} accessible={false}>
            <Animated.View style={[style.heartbeatBorder, heartbeatAnimatedStyle]} accessible={false} />
            <Icon name={ICON_NAMES.NotificationBadge} />
          </View>
        )
      }
    />
  );

  // only for MVP4 we don't use hover tooltip
  return isMVP4 ? (
    renderer
  ) : (
    <BaseHoverTooltip
      tooltipID={headerEditorTestID(TestIDs.PHOTO_DRAWER, 'hover')}
      TooltipContent={
        !isDesktop ? () => <TextLato style={{ padding: 10 }}>{i18n.t('header:photo')}</TextLato> : undefined
      }
      isArrowVisible={false}
    >
      {renderer}
    </BaseHoverTooltip>
  );
});
